import type { MouseEvent as ReactMouseEvent } from 'react';
import type { ResizeDir } from '@/hooks/useResizableModal';

/**
 * Edge + corner drag handles for a resizable modal. Render as the last children
 * of a `position: relative/fixed` modal box; pass the `startResize` factory from
 * useResizableModal. Hidden when the modal is fullscreen.
 */

const HANDLES: { dir: ResizeDir; className: string }[] = [
  // Side edges (8px hit strips)
  { dir: 'n', className: 'top-0 left-0 right-0 h-2 cursor-ns-resize' },
  { dir: 's', className: 'bottom-0 left-0 right-0 h-2 cursor-ns-resize' },
  { dir: 'e', className: 'top-0 bottom-0 right-0 w-2 cursor-ew-resize' },
  { dir: 'w', className: 'top-0 bottom-0 left-0 w-2 cursor-ew-resize' },
  // Corners (16px squares, rendered after edges so they win at overlaps)
  { dir: 'nw', className: 'top-0 left-0 h-4 w-4 cursor-nwse-resize' },
  { dir: 'ne', className: 'top-0 right-0 h-4 w-4 cursor-nesw-resize' },
  { dir: 'sw', className: 'bottom-0 left-0 h-4 w-4 cursor-nesw-resize' },
  { dir: 'se', className: 'bottom-0 right-0 h-4 w-4 cursor-nwse-resize' },
];

export function ModalResizeHandles({
  onStart,
  dirs,
}: {
  onStart: (dir: ResizeDir) => (event: ReactMouseEvent) => void;
  /** Restrict which handles render (default: all 8). Useful for modals whose
   *  top edge holds a header/close button — pass e.g. ['s','e','w','se','sw']. */
  dirs?: ResizeDir[];
}) {
  const handles = dirs ? HANDLES.filter((h) => dirs.includes(h.dir)) : HANDLES;
  return (
    <>
      {handles.map(({ dir, className }) => (
        <div
          key={dir}
          onMouseDown={onStart(dir)}
          className={`absolute z-[60] select-none ${className}`}
          aria-hidden="true"
        />
      ))}
    </>
  );
}
